import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { AbilityServiceSignal } from '@casl/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { captureException } from '@sentry/angular';
import { AuthorizationModule } from '@wilson/authorization';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import {
  FeatureName,
  RefreshOptions,
  RoleAction,
  RolePermissionSubject,
  ShiftState,
  ShiftWithAzmAttributesAndUser,
} from '@wilson/interfaces';
import {
  ActionMenuExportShiftReportTranslationPipe,
  SelectClientForShiftsExportReportsHelperService,
  ShiftsActionsMenuHelperService,
} from '@wilson/non-domain-specific/shift-common-actions';
import {
  MAX_SELECTABLE_SHIFTS,
  MAX_SELECTABLE_SHIFTS_FOR_ADDING_ATTACHMENTS,
} from '@wilson/non-domain-specific/shift-helpers/core';
import { AddPayrollTransactionsComponent } from '@wilson/payroll/shared';
import { ShiftsService } from '@wilson/shifts';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'wilson-work-time-management-actions',
  templateUrl: './work-time-management-actions.component.html',
  styleUrls: ['./work-time-management-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    NzButtonModule,
    NzDropDownModule,
    NzIconModule,
    FeatureFlagsModule,
    NzMenuModule,
    AuthorizationModule,
    NzTooltipModule,
    ActionMenuExportShiftReportTranslationPipe,
  ],
  providers: [ShiftsActionsMenuHelperService],
})
export class WorkTimeManagementActionsComponent implements OnChanges {
  private readonly translate = inject(TranslateService);
  private readonly shiftsService = inject(ShiftsService);
  private readonly messageService = inject(NzMessageService);
  private readonly nzModalService = inject(NzModalService);
  protected readonly selectClientForShiftsExportReportsHelperService = inject(
    SelectClientForShiftsExportReportsHelperService,
  );
  private readonly shiftsActionsMenuHelperService = inject(
    ShiftsActionsMenuHelperService,
  );

  @Input() selectedRowsCount = 0;
  @Input() selectedRows: ShiftWithAzmAttributesAndUser[] = [];
  @Input() haveSelectedRows = false;
  @Input() selectedRowsCanBeTimeAdjusted = false;
  @Input() selectedRowsCanBeAcceptedOrSentBack = false;
  @Input() selectedRowsCanBeAddPayrollTransactions = false;

  @Output() refresh = new EventEmitter<RefreshOptions>();
  @Output() updateSelectedShifts = new EventEmitter();

  protected ShiftState = ShiftState;
  protected can = inject(AbilityServiceSignal).can;

  protected FeatureName = FeatureName;
  protected RoleAction = RoleAction;
  protected RolePermissionSubject = RolePermissionSubject;
  protected isEveryShiftSubmittedSignal = signal(false);
  protected MAX_SELECTABLE_SHIFTS = MAX_SELECTABLE_SHIFTS;
  protected MAX_SELECTABLE_SHIFTS_FOR_ADDING_ATTACHMENTS =
    MAX_SELECTABLE_SHIFTS_FOR_ADDING_ATTACHMENTS;

  ngOnChanges(changes: SimpleChanges): void {
    if (this.selectedRows?.length && changes['selectedRows']) {
      const submittedStates = [
        ShiftState.Submitted,
        ShiftState.AcceptedTimes,
        ShiftState.SubmittedToPayrollProvider,
      ];

      const isEveryShiftSubmitted = this.selectedRows?.every((shift) =>
        submittedStates.includes(shift.state),
      );

      this.isEveryShiftSubmittedSignal.set(isEveryShiftSubmitted);
    }
  }

  protected async updateSelectedShiftsAdjustTime(): Promise<void> {
    if (this.selectedRows) {
      const shiftIdsToUpdate: string[] = [];
      this.selectedRows.forEach((row) => {
        shiftIdsToUpdate.push(row.id as string);
      });
      try {
        if (shiftIdsToUpdate.length) {
          await firstValueFrom(
            this.shiftsService.patchBulkSubmitShifts(shiftIdsToUpdate),
          );
          this.messageService.success(
            this.translate.instant('popup.save.text'),
          );
          this.refresh.next({ shouldPreserveSelection: false });
        }
      } catch {
        this.messageService.error(
          this.translate.instant('general.something_went_wrong'),
        );
      }
    }
  }

  protected openManualTransactionModal(): void {
    if (this.selectedRows) {
      const modalRef: NzModalRef<AddPayrollTransactionsComponent> =
        this.nzModalService.create<AddPayrollTransactionsComponent>({
          nzTitle: this.translate.instant(
            'work_time_management.drawer.add_payroll_transaction',
          ),
          nzContent: AddPayrollTransactionsComponent,
          nzClosable: true,
          nzCentered: true,
          nzAfterClose: this.refresh,
        });
      if (modalRef.componentInstance) {
        modalRef.componentInstance.selectedShifts = this.selectedRows;
      }
    }
  }
  protected async handleBulkShiftExport({
    hasBillingFeaturePlan,
    hasClientReadPermission,
  }: {
    hasBillingFeaturePlan: boolean;
    hasClientReadPermission: boolean;
  }): Promise<void> {
    const selectedShiftIds = this.selectedRows.map((shift) => shift.id);
    if (hasClientReadPermission && hasBillingFeaturePlan) {
      const selectedShiftsAgreementIds = this.selectedRows.reduce(
        (acc, shift) => acc.concat(shift.agreementIds),
        [] as string[],
      );
      try {
        const defaultClientId =
          (await this.selectClientForShiftsExportReportsHelperService.getFirstSelectedShiftClientId(
            { selectedShiftId: selectedShiftIds[0] },
          )) || '';

        await this.selectClientForShiftsExportReportsHelperService.openClientSelectModal(
          {
            selectedShiftsAgreementIds,
            selectedShiftIds,
            defaultClientId,
          },
        );
      } catch (error) {
        captureException(error);
        this.messageService.error(
          this.translate.instant('general.something_went_wrong'),
        );
      }
    } else {
      await this.selectClientForShiftsExportReportsHelperService.bulkExportShiftsPdfWithoutClient(
        selectedShiftIds,
      );
    }
  }

  async openAttachmentsModal(): Promise<void> {
    const selectedShiftIds = this.selectedRows.map((shift) => shift.id);
    const result =
      await this.shiftsActionsMenuHelperService.addAttachments(
        selectedShiftIds,
      );

    if (result) {
      this.refresh.next({ shouldPreserveSelection: true });
    }
  }

  async openReportsModal(): Promise<void> {
    const selectedShiftIds = this.selectedRows.map((shift) => shift.id);
    const result =
      await this.shiftsActionsMenuHelperService.addShiftReports(
        selectedShiftIds,
      );

    if (result) {
      this.refresh.next({ shouldPreserveSelection: true });
    }
  }
}
